import { CdkDrag, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop'
import { NgClass, NgFor, NgIf } from '@angular/common'
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core'
import { RouterLink } from '@angular/router'
import { ClickOutsideDirective } from '@common'
import { ContextMenuComponent, TooltipComponent } from '@components'
import { EluxButton, EluxIcon, EluxPaginator, PaginationEvent } from '@elxjs/ui'
import { UnsavedModalService } from '@services'

export interface TableHeader {
  name?: string
  type: 'drag' | 'new-value' | 'value' | 'tags' | 'action' | 'order' | 'status'
  info?: string
  key?: string
  align?: 'left' | 'center' | 'right'
  actions?: ('edit' | 'duplicate' | 'delete')[]
  width?: string
  labelGreen?: 'Ativo' | 'Publicado'
  labelRed?: 'Inativo' | 'Desativado' | 'Expirado' | 'Rascunho'
  labelBlue?: 'Agendado'
  labelYellow?: 'Rascunho'
}

export interface TableGroup {
  totalPages?: number
  currentPage?: number
  rows: TableRow[]
  prefix?: string
  newId?: string
}

export interface TableRow {
  object: any
  showAction?: boolean
}

export interface Tooltip {
  key: string
  show: boolean
  content: string
}

@Component({
  selector: 'app-table',
  standalone: true,
  imports: [
    NgFor,
    EluxIcon,
    TooltipComponent,
    ContextMenuComponent,
    ClickOutsideDirective,
    EluxButton,
    NgClass,
    NgIf,
    EluxPaginator,
    RouterLink,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
  ],
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent implements OnInit, AfterViewInit {
  @Input() headers: TableHeader[] = []
  @Input() tableData: TableGroup[] = []
  @Output() onDelete: EventEmitter<string> = new EventEmitter<string>()
  @Output() onCancel: EventEmitter<void> = new EventEmitter<void>()
  @Output() onSave: EventEmitter<TableGroup[]> = new EventEmitter<TableGroup[]>()
  @Output() onPaginationEvent: EventEmitter<PaginationEvent> = new EventEmitter<PaginationEvent>()
  @Output() onChange: EventEmitter<boolean> = new EventEmitter<boolean>()

  protected tooltips: Tooltip[] = []
  protected change: boolean = false

  @ViewChild('scrollWrapper', { static: true }) scrollWrapper!: ElementRef<HTMLDivElement>

  isScrollAtStart = true
  isScrollAtEnd = false

  activeGroupIndex: number | null = null

  constructor(private unsavedModal: UnsavedModalService) {}

  async ngOnInit(): Promise<void> {
    for (let i = 0; i < this.headers.length; i++) {
      const header = this.headers[i]
      if (header.info) {
        this.tooltips.push({
          key: 'tooltip_' + header.key,
          show: false,
          content: header.info,
        })
      }
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.updateScrollFlags()
    })
  }

  hasColumnDrag() {
    return this.headers.some((x) => x.type == 'drag')
  }

  isGroupDisabled(g: number): boolean {
    return this.activeGroupIndex !== null && this.activeGroupIndex !== g
  }

  mouseEnterTooltip(head: TableHeader) {
    for (let i = 0; i < this.tooltips.length; i++) {
      if (this.tooltips[i].key == 'tooltip_' + head.key) {
        this.tooltips[i].show = true
      }
    }
  }

  mouseLeaveTooltip() {
    for (let i = 0; i < this.tooltips.length; i++) this.tooltips[i].show = false
  }

  deleteRow(rowId: string) {
    this.onDelete.emit(rowId)
  }

  async cancel() {
    const canLeave = await this.unsavedModal.askUser()
    if (canLeave) this.onCancel.emit()
  }

  save() {
    this.onSave.emit(this.tableData)
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent) {
    if (this.change) {
      event.preventDefault()
      event.returnValue = ''
    }
  }

  getHeaderWidth(head: TableHeader): string | null {
    if (head.type == 'drag') return '72px'
    else if (head.type == 'action') return '100px'

    return head.width ? head.width : ''
  }

  getActions(): ('edit' | 'duplicate' | 'delete')[] {
    const headAction = this.headers.find((h) => h.type === 'action')
    return headAction && headAction.actions ? headAction.actions : []
  }

  onTableScroll(event: Event): void {
    this.updateScrollFlags()
  }

  private updateScrollFlags() {
    const el = this.scrollWrapper.nativeElement

    this.isScrollAtStart = el.scrollLeft === 0

    this.isScrollAtEnd = Math.ceil(el.scrollLeft + el.clientWidth) >= el.scrollWidth
  }

  scrollHorizontally(direction: 'left' | 'right') {
    const el = this.scrollWrapper.nativeElement
    const step = direction === 'left' ? -300 : 300

    el.scrollBy({ left: step, behavior: 'smooth' })

    setTimeout(() => this.updateScrollFlags(), 300)
  }

  drop(event: any, group: number) {
    moveItemInArray(this.tableData[group].rows, event.previousIndex, event.currentIndex)
    this.change = true
    this.onChange.emit(this.change)
  }

  onDragStart(groupIndex: number) {
    this.activeGroupIndex = groupIndex
  }

  onDragEnd() {
    this.activeGroupIndex = null
  }

  toggleSort(head: TableHeader) {
    return false
  }
}
